// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import FormField, {
    FormFieldClickable,
    FormFieldComponentProps,
    FormFieldIcon,
    FormFieldPresentationProps,
    FormFieldSizeVariant,
} from "@conklin/MobiusOverrides/FormField";
import { MAX_QTY } from "@conklin/XDL/constants";
import IconClose from "@conklin/XDL/Icons/IconClose";
import {
    useMask,
    xDefaultRules,
    xFieldValidator,
    xMapTypeToDomType,
    xValidateFieldParams,
    xValidationProps,
} from "@conklin/XDL/Utilities";
import translate from "@insite/client-framework/Translate";
import { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import AnimatedTextField from "@insite/mobius/TextField/AnimatedTextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { HasDisablerContext, withDisabler } from "@insite/mobius/utilities/DisablerContext";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import { MobiusStyledComponentPropsWithRef } from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import styled, { css, ThemeConsumer } from "styled-components";

export interface TextFieldPresentationProps extends FormFieldPresentationProps<TextFieldComponentProps> {
    /** Props to be passed into the Clickable component that will optionally wrap the icon.
     * If `clickableProps` is passed, the icon will be wrapped in a Clickable. If not, the icon will not be clickable. */
    iconClickableProps?: ClickablePresentationProps;
    /** The props for icon displaying on the text input field.
     * @themable */
    /** XNG-Changes: add position */
    iconProps?: IconPresentationProps & { position?: "left" | "right" };
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<TextFieldComponentProps>;
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** Background color of the form element */
    backgroundColor?: string;
    /** The props for the textfield label */
    labelProps?: TypographyPresentationProps;
    /** Boolean value for width animation */
    isAnimated?: boolean;
}

type TextFieldComponentProps = MobiusStyledComponentPropsWithRef<
    "input",
    { ref?: React.Ref<HTMLInputElement | null> },
    {
        /** The props for icon displaying on the text input field. */
        iconProps?: IconPresentationProps;
        /** Props to be passed into the Clickable component that will optionally wrap the icon.
         * If `clickableProps` is passed, the icon will be wrapped in a Clickable. If not, the icon will not be clickable. */
        iconClickableProps?: ClickableProps;
        /** Visually hidden text to be rendered within the text field clickable. */
        clickableText?: string;
        /** Error message to be displayed below the input. */
        error?: React.ReactNode;
        /** Hint text to be displayed below the input. */
        hint?: React.ReactNode;
        /** Label to be displayed above the input. */
        label?: React.ReactNode;
        // XNG-Changes
        showError?: boolean;
        showClear?: boolean;
        /** XNG-Changes: extra Type to handle showPassword case. */
        xType?: string;
        /** XNG-Changes: extra prop to handle show label with required text case. */
        showLabelWithRequiredText?: boolean;
    } & Partial<FormFieldComponentProps> &
        // XNG-Changes: required for validation
        xValidationProps
>;

export type TextFieldProps = TextFieldComponentProps & TextFieldPresentationProps;

export const InputStyle = styled.input<InjectableCss>`
    ${injectCss}
`;

const omitKeys = [
    "sizeVariant",
    "border",
    "label",
    "labelPosition",
    "theme",
    "cssOverrides",
    "iconProps",
    "backgroundColor",
    "labelProps",
    "dispatch",
    "labelId",
    "isAnimated",
    "aria-expanded",
    "aria-controls",
] as (keyof Omit<
    TextFieldProps,
    | "id"
    | "type"
    | "onKeyDown"
    | "onChange"
    | "clickableText"
    | "disabled"
    | "error"
    | "hint"
    | "iconClickableProps"
    | "id"
    | "placeholder"
    | "required"
    | "disable"
    | "mergeCss"
    | "min"
    | "max"
    | "css"
    | "isAnimated"
    // XNG-Changes: show or hide error option
    | "showError"
    | "showClear"
    | "xType"
    // XNG-Changes: omit validation keys
    | keyof xValidationProps
>)[];

const validForNumber = ({ altKey, ctrlKey, key }: React.KeyboardEvent<HTMLInputElement>) => {
    return altKey || ctrlKey || key.length > 1 || /[0-9\.\,\-\+]/.test(key);
};

const validNumber = (event: React.ChangeEvent<HTMLInputElement>, min?: number | string, max?: number | string) => {
    const number = Number(event.currentTarget.value);
    if (min !== undefined && number < Number(min)) {
        return false;
    }

    if (max !== undefined && number > Number(max)) {
        return false;
    }

    return true;
};

const validQty = (event: React.ChangeEvent<HTMLInputElement>) => {
    const number = Number(event.currentTarget.value);
    const schema = xDefaultRules["qty"];
    let min = 0;
    let max = MAX_QTY;

    schema?.split("|").forEach(item => {
        const [attr, val] = item.split(":");
        if (attr === "min") {
            min = +val;
        }
        if (attr === "max") {
            max = +val;
        }
    }, {});
    if (Number.isNaN(number)) {
        return false;
    }

    if (event.currentTarget.value.includes(".")) {
        return false;
    }

    if (number > max || number < min) {
        return false;
    }

    return true;
};

/**
 * TextField is a form element with an optional label, hint text, error message and optional icon.
 * Props not contained in the list below get passed into the input component (e.g. event handlers, `value`, etc).
 */
const TextField: React.FC<TextFieldProps & HasDisablerContext> = props => {
    // <XNG-Changes>: required for validation
    const [internalError, setInternalError] = React.useState<string>("");
    const [touched, setTouched] = React.useState<boolean>(false);
    // </XNG-Changes>

    return (
        <ThemeConsumer>
            {(theme?: BaseTheme) => {
                const {
                    // XNG-Changes: default it to text (for validation)
                    type: typeProp = "text",
                    xType,
                    onKeyDown,
                    onChange,
                    clickableText,
                    disable,
                    disabled,
                    // XNG-Changes: rename it to errorProp
                    error: errorProp,
                    hint,
                    iconClickableProps,
                    id,
                    placeholder = " ",
                    required,
                    mergeCss,
                    min,
                    max,
                    isAnimated,
                    // XNG-Changes: destruct validation props
                    validationRules,
                    formId,
                    disableMask,
                    messageLabel,
                    showError = true,
                    showClear = false,
                    ...otherProps
                } = props;

                // <XNG-Changes>: required for validation
                const error = errorProp || internalError;
                const typeForValidation = xDefaultRules[(xType || typeProp) as keyof typeof xDefaultRules]
                    ? xType || typeProp
                    : "text";
                const type = xDefaultRules[typeProp as keyof typeof xDefaultRules] ? typeProp : "text";
                const fieldValidator = xFieldValidator(
                    {
                        label: messageLabel || (otherProps.label as string),
                        type: typeForValidation as xValidateFieldParams["type"],
                        validationRules,
                        value: otherProps.value || otherProps.defaultValue,
                        onError: (errorMsg: string) => {
                            setInternalError(errorMsg);
                            otherProps.onError?.(errorMsg as any);
                        },
                        required,
                        touched,
                    },
                    formId,
                );
                const internalOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
                    fieldValidator.onBlur(event);
                    otherProps.onBlur?.(event);
                    setTouched(true);
                };
                const internalKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
                    fieldValidator.onKeyUp(event);
                    otherProps.onKeyUp?.(event);
                };
                // </XNG-Changes>
                const isDisabled = !!(disable || disabled);
                const inputId = id || uniqueId();
                const labelId = `${inputId}-label`;
                const inputLabelObj = otherProps.label === 0 || otherProps.label ? { "aria-labelledby": labelId } : {};
                const descriptionId = `${inputId}-description`;
                const hasDescription = error || hint;

                const { applyProp, spreadProps, applyStyledProp } = applyPropBuilder(
                    { theme, ...otherProps },
                    { component: "textField", category: "formField" },
                );
                const labelProps = applyProp("labelProps");
                const sizeVariant: FormFieldSizeVariant = applyProp("sizeVariant", "default");
                const iconProps = spreadProps("iconProps");
                const resolvedMergeCss = mergeCss ?? theme?.textField?.defaultProps?.mergeCss;

                const internalOnKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (typeForValidation === "number" && !validForNumber(event)) {
                        event.preventDefault();
                    }

                    onKeyDown?.(event);
                };

                const internalOnChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
                    if (typeForValidation === "number" && !validNumber(event, min, max)) {
                        event.preventDefault();
                        return;
                    }
                    if (typeForValidation === "qty" && !validQty(event)) {
                        event.preventDefault();
                        return;
                    }
                    if (disableMask) {
                        onChange?.(event);
                        return;
                    }
                    const schema = validationRules || xDefaultRules[typeForValidation as keyof typeof xDefaultRules];
                    // XNG-Changes: using oninput if onchange is undefined
                    useMask(schema || "", otherProps.value as string, onChange || otherProps.onInput)(event);
                };

                const formFieldProps = {
                    ...iconProps,
                    size: sizeVariantValues[sizeVariant].icon,
                    color: isDisabled ? "text.disabled" : iconProps.color || "common.ui5",
                };

                const formIcon = iconProps ? (
                    iconClickableProps ? (
                        <FormFieldClickable {...iconClickableProps} disabled={isDisabled}>
                            <FormFieldIcon {...formFieldProps} />
                            <VisuallyHidden>{clickableText}</VisuallyHidden>
                        </FormFieldClickable>
                    ) : (
                        <FormFieldIcon {...formFieldProps} />
                    )
                ) : null;

                const InputElement = !isAnimated ? (
                    <>
                        <InputStyle
                            {...{
                                disabled: isDisabled,
                                placeholder,
                                // XNG-Changes: if has formId then don't let the browser to check required
                                required,
                            }}
                            {...inputLabelObj}
                            {...omitMultiple(otherProps, omitKeys)}
                            ref={props.ref}
                            min={min}
                            max={max}
                            // XNG-Changes: validation - change type value to the mapped one
                            type={xMapTypeToDomType[type] || "text"}
                            id={inputId}
                            onKeyDown={internalOnKeyDownHandler}
                            onChange={internalOnChangeHandler}
                            aria-labelledby={labelId}
                            aria-describedby={hasDescription ? descriptionId : undefined}
                            aria-invalid={!!error}
                            aria-required={!disabled && required}
                            tabIndex={0}
                            css={applyStyledProp("css", resolvedMergeCss)}
                            // XNG-Changes: required for validation
                            onBlur={internalOnBlur}
                            onKeyUp={internalKeyUp}
                        />
                        {formIcon}
                        {/** XNG-Changes */}
                        {showClear && !isDisabled && (
                            <FormFieldClickable
                                {...iconClickableProps}
                                {...(formIcon
                                    ? {
                                          css: css`
                                              &&& {
                                                  right: 20px;
                                              }
                                          `,
                                          onClick: () =>
                                              onChange?.({
                                                  currentTarget: { value: "" },
                                                  target: { value: "" },
                                              } as any),
                                      }
                                    : {})}
                                className="clear"
                            >
                                <FormFieldIcon {...{ ...formFieldProps, size: 12, src: IconClose }} />
                                <VisuallyHidden>{translate("Clear")}</VisuallyHidden>
                            </FormFieldClickable>
                        )}
                    </>
                ) : (
                    <AnimatedTextField
                        {...{
                            disabled: isDisabled,
                            placeholder,
                            // XNG-Changes: if has formId then don't let the browser to check required
                            required,
                        }}
                        {...inputLabelObj}
                        {...omitMultiple(otherProps, omitKeys)}
                        min={min}
                        max={max}
                        id={inputId}
                        onKeyDown={internalOnKeyDownHandler}
                        onChange={internalOnChangeHandler}
                        aria-labelledby={labelId}
                        aria-describedby={hasDescription ? descriptionId : undefined}
                        aria-invalid={!!error}
                        aria-required={!disabled && required}
                        tabIndex={0}
                        css={applyStyledProp("css", resolvedMergeCss)}
                        // XNG-Changes: required for validation
                        onBlur={internalOnBlur}
                        type={xMapTypeToDomType[type] || "text"}
                    />
                );

                return (
                    <FormField
                        {...props}
                        /**
                         * XNG-Changes: validation
                         * if passed formId then don't let the browser to check 'required'
                         * */
                        required={required}
                        labelProps={labelProps}
                        descriptionId={descriptionId}
                        formInput={InputElement}
                        labelId={labelId}
                        inputId={inputId}
                        // XNG-Changes: validaton - pass the determined error
                        error={showError && error}
                        // XNG-Changes: add icon position if there is an icon passed
                        iconPosition={props.iconProps?.src ? props.iconProps?.position || "right" : undefined}
                    />
                );
            }}
        </ThemeConsumer>
    );
};

TextField.displayName = "TextField";
/** @component */
export default withDisabler(TextField);
